import { Peer } from 'peerjs';
import QRCode from 'qrcode';
import { state } from './state.js';

export const mpState = {
    isMultiplayer: false,
    role: null, // 'host' or 'client'
    peer: null,
    conn: null, // Client connection to Host
    connections: [], // Host connections to Clients (max 3)
    roomId: null,
    isConnected: false,
    playerId: 0, // 0 for Host, 1-3 for Clients
    players: [], // active players: { id, name, ready, finished, completedAll, strokes, holeIndex, finalStrokes, finalTime, ballState }
    courseLevels: [], // pre-generated course levels
    startTime: 0,
    endTime: 0
};

function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 255, g: 255, b: 255 };
}

// Global hook to broadcast visual customizations inside WebRTC matchmaking
window.syncPlayerCustomizationToMultiplayer = () => {
    if (mpState.isMultiplayer && mpState.isConnected) {
        const rgb = hexToRgb(state.customBallColor || '#FFFFFF');
        
        if (mpState.role === 'host') {
            const hostPlayer = mpState.players.find(p => p.id === 0);
            if (hostPlayer) {
                hostPlayer.ballState.colorR = rgb.r;
                hostPlayer.ballState.colorG = rgb.g;
                hostPlayer.ballState.colorB = rgb.b;
                hostPlayer.ballState.skin = state.customBallSkin;
                hostPlayer.ballState.trailEffect = state.customBallTrail;
            }
            sendPacket({
                type: 'players_list',
                players: mpState.players.map(p => ({
                    id: p.id,
                    name: p.name,
                    ready: p.ready,
                    colorR: p.ballState.colorR,
                    colorG: p.ballState.colorG,
                    colorB: p.ballState.colorB,
                    skin: p.id === 0 ? state.customBallSkin : (p.ballState.skin || 'neon'),
                    trailEffect: p.id === 0 ? state.customBallTrail : (p.ballState.trailEffect || 'neon')
                }))
            });
        } else {
            sendPacket({
                type: 'join_info',
                playerId: mpState.playerId,
                name: state.currentPlayerName,
                colorR: rgb.r,
                colorG: rgb.g,
                colorB: rgb.b,
                skin: state.customBallSkin,
                trailEffect: state.customBallTrail
            });
        }
    }
};

let messageCallback = null;

export function registerMPMessageCallback(callback) {
    messageCallback = callback;
}

export function generateRoomId() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Avoid ambiguous chars
    let result = '';
    for (let i = 0; i < 5; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
}

export function initHost(roomId, onReady, onError) {
    mpState.isMultiplayer = true;
    mpState.role = 'host';
    mpState.roomId = roomId;
    mpState.isConnected = false;
    mpState.playerId = 0; // Host is Player 1 (slot 0)
    mpState.connections = [];
    
    // Initialize host as first player in list with their custom ball color
    const hostColor = hexToRgb(state.customBallColor || '#FFFFFF');
    mpState.players = [{
        id: 0,
        name: state.currentPlayerName,
        ready: true, // Host is always ready
        finished: false,
        completedAll: false,
        strokes: 0,
        holeIndex: 0,
        finalStrokes: 0,
        finalTime: 0,
        ballState: { x: 0, y: 0, vx: 0, vy: 0, r: 8, isSinking: false, colorR: hostColor.r, colorG: hostColor.g, colorB: hostColor.b, trail: [] }
    }];

    const peerId = 'space-golf-' + roomId;
    console.log(`Initializing PeerJS Host with ID: ${peerId}`);
    
    mpState.peer = new Peer(peerId, {
        host: '0.peerjs.com',
        port: 443,
        secure: true,
        debug: 1
    });

    mpState.peer.on('open', (id) => {
        console.log(`Host peer active. ID: ${id}`);
        
        // Render Join QR code
        const joinUrl = `${window.location.origin}${window.location.pathname}?join=${roomId}`;
        const qrCanvas = document.getElementById('mpQrCanvas');
        if (qrCanvas) {
            QRCode.toCanvas(qrCanvas, joinUrl, { width: 180, margin: 2 }, (err) => {
                if (err) console.error("QR generation fail:", err);
            });
        }
        
        const joinLink = document.getElementById('mpJoinLink');
        if (joinLink) {
            joinLink.href = joinUrl;
            joinLink.textContent = joinUrl;
        }

        if (onReady) onReady(roomId);
    });

    mpState.peer.on('connection', (connection) => {
        if (mpState.connections.length >= 3) {
            console.warn("Host received join request but room is full (max 4 players)!");
            connection.on('open', () => {
                connection.send({ type: 'room_full' });
                setTimeout(() => connection.close(), 1000);
            });
            return;
        }
        
        console.log("Client connecting...");
        mpState.connections.push(connection);
        setupHostConnection(connection, onError);
    });

    mpState.peer.on('error', (err) => {
        console.error("Host PeerJS Error:", err);
        if (onError) onError(err.message || err.type || "Peer connection failed.");
    });
}

export function initClient(roomId, onReady, onError) {
    mpState.isMultiplayer = true;
    mpState.role = 'client';
    mpState.roomId = roomId;
    mpState.isConnected = false;
    mpState.playerId = 1; // Default client slot 1, updated on assign_id
    mpState.connections = [];
    mpState.players = [];

    console.log(`Initializing PeerJS Client. Attempting to connect to room: ${roomId}`);
    
    mpState.peer = new Peer({
        host: '0.peerjs.com',
        port: 443,
        secure: true,
        debug: 1
    });

    mpState.peer.on('open', (id) => {
        console.log(`Client peer active. Connecting to Host space-golf-${roomId}...`);
        
        // Render Join QR code for the Client as well!
        const joinUrl = `${window.location.origin}${window.location.pathname}?join=${roomId}`;
        const qrCanvas = document.getElementById('mpQrCanvas');
        if (qrCanvas) {
            QRCode.toCanvas(qrCanvas, joinUrl, { width: 180, margin: 2 }, (err) => {
                if (err) console.error("QR generation fail:", err);
            });
        }
        
        const joinLink = document.getElementById('mpJoinLink');
        if (joinLink) {
            joinLink.href = joinUrl;
            joinLink.textContent = joinUrl;
        }

        mpState.conn = mpState.peer.connect('space-golf-' + roomId, {
            reliable: true
        });
        setupClientConnection(mpState.conn, onReady, onError);
    });

    mpState.peer.on('error', (err) => {
        console.error("Client PeerJS Error:", err);
        if (onError) onError(err.message || err.type || "Peer connection failed.");
    });
}

function setupHostConnection(conn, onError) {
    const assignedId = mpState.connections.length; // 1, 2, or 3 (index in connections list)
    
    conn.on('open', () => {
        console.log(`Host Connection with Client ${assignedId} active!`);
        mpState.isConnected = true;
        
        document.getElementById('mpConnectingMsg').style.display = 'none';
        document.getElementById('mpLobbyScreen').style.display = 'block';
        document.getElementById('mpStartBtn').style.display = 'inline-block';
        document.getElementById('mpReadyBtn').style.display = 'none';

        // Assign ID
        conn.send({ type: 'assign_id', playerId: assignedId });
        
        // Add to players list
        const clientColors = [
            { r: 100, g: 255, b: 100 }, // Green for Player 2
            { r: 100, g: 200, b: 255 }, // Blue for Player 3
            { r: 255, g: 100, b: 255 }  // Pink/Magenta for Player 4
        ];
        const color = clientColors[assignedId - 1] || { r: 255, g: 255, b: 255 };
        
        mpState.players = mpState.players.filter(p => p.id !== assignedId);
        mpState.players.push({
            id: assignedId,
            name: `Player ${assignedId + 1}`,
            ready: false,
            finished: false,
            completedAll: false,
            strokes: 0,
            holeIndex: 0,
            finalStrokes: 0,
            finalTime: 0,
            ballState: { x: 0, y: 0, vx: 0, vy: 0, r: 8, isSinking: false, colorR: color.r, colorG: color.g, colorB: color.b, trail: [] }
        });
        
        // Broadcast updated players list to everyone
        sendPacket({
            type: 'players_list',
            players: mpState.players.map(p => ({ id: p.id, name: p.name, ready: p.ready }))
        });
        
        updateLobbyUI();
    });

    conn.on('data', (data) => {
        if (!data || typeof data !== 'object') return;
        
        if (data.playerId === undefined) {
            data.playerId = assignedId;
        }

        // Process locally
        handleIncomingPacket(data);

        // Relay to all OTHER clients
        mpState.connections.forEach(c => {
            if (c.peer !== conn.peer && c.open) {
                try { c.send(data); } catch(e) {}
            }
        });
    });

    conn.on('close', () => {
        console.warn(`Host Connection closed by client ${assignedId}.`);
        mpState.connections = mpState.connections.filter(c => c.peer !== conn.peer);
        mpState.players = mpState.players.filter(p => p.id !== assignedId);
        
        sendPacket({
            type: 'players_list',
            players: mpState.players.map(p => ({ id: p.id, name: p.name, ready: p.ready }))
        });
        
        if (mpState.connections.length === 0) {
            mpState.isConnected = false;
        }
        updateLobbyUI();
    });

    conn.on('error', (err) => {
        console.error("Host connection error:", err);
    });
}

function setupClientConnection(conn, onReady, onError) {
    conn.on('open', () => {
        console.log("Client connected to Host!");
        mpState.isConnected = true;
        
        document.getElementById('mpConnectingMsg').style.display = 'none';
        document.getElementById('mpLobbyScreen').style.display = 'block';
        document.getElementById('mpStartBtn').style.display = 'none';
        document.getElementById('mpReadyBtn').style.display = 'inline-block';
        
        // Request ID by sending join name, custom color, skin, and trail
        const rgb = hexToRgb(state.customBallColor || '#FFFFFF');
        conn.send({ type: 'join_info', name: state.currentPlayerName, colorR: rgb.r, colorG: rgb.g, colorB: rgb.b, skin: state.customBallSkin, trailEffect: state.customBallTrail });
        
        if (onReady) onReady();
    });

    conn.on('data', (data) => {
        if (!data || typeof data !== 'object') return;
        handleIncomingPacket(data);
    });

    conn.on('close', () => {
        console.warn("Client Connection closed by Host.");
        mpState.isConnected = false;
        if (onError) onError("Host disconnected.");
    });

    conn.on('error', (err) => {
        console.error("Client connection error:", err);
        if (onError) onError(err.message || "Connection failed.");
    });
}

function handleIncomingPacket(data) {
    const senderId = data.playerId;
    if (senderId === undefined) return;

    let player = mpState.players.find(p => p.id === senderId);
    if (!player && data.type !== 'players_list' && data.type !== 'assign_id') {
        const clientColors = [
            { r: 100, g: 255, b: 100 },
            { r: 100, g: 200, b: 255 },
            { r: 255, g: 100, b: 255 }
        ];
        const color = clientColors[senderId - 1] || { r: 255, g: 255, b: 255 };
        player = {
            id: senderId,
            name: `Player ${senderId + 1}`,
            ready: false,
            finished: false,
            completedAll: false,
            strokes: 0,
            holeIndex: 0,
            finalStrokes: 0,
            finalTime: 0,
            ballState: { x: 0, y: 0, vx: 0, vy: 0, r: 8, isSinking: false, colorR: color.r, colorG: color.g, colorB: color.b, skin: 'neon', trailEffect: 'neon', trail: [] }
        };
        mpState.players.push(player);
    }

    switch (data.type) {
        case 'assign_id':
            mpState.playerId = data.playerId;
            console.log(`Assigned PlayerId: ${mpState.playerId}`);
            // Send back join info with correct player ID, name, color, skin, and trail
            const assignRgb = hexToRgb(state.customBallColor || '#FFFFFF');
            sendPacket({ type: 'join_info', playerId: mpState.playerId, name: state.currentPlayerName, colorR: assignRgb.r, colorG: assignRgb.g, colorB: assignRgb.b, skin: state.customBallSkin, trailEffect: state.customBallTrail });
            break;

        case 'join_info':
            if (player) {
                player.name = data.name;
                if (data.colorR !== undefined) player.ballState.colorR = data.colorR;
                if (data.colorG !== undefined) player.ballState.colorG = data.colorG;
                if (data.colorB !== undefined) player.ballState.colorB = data.colorB;
                if (data.skin !== undefined) player.ballState.skin = data.skin;
                if (data.trailEffect !== undefined) player.ballState.trailEffect = data.trailEffect;
                console.log(`Player ${senderId} joined as: ${data.name} with skin ${data.skin} and trail ${data.trailEffect}`);
                
                // Host broadcasts the fully updated name and customization list to everyone
                if (mpState.role === 'host') {
                    sendPacket({
                        type: 'players_list',
                        players: mpState.players.map(p => ({
                            id: p.id,
                            name: p.name,
                            ready: p.ready,
                            colorR: p.ballState.colorR,
                            colorG: p.ballState.colorG,
                            colorB: p.ballState.colorB,
                            skin: p.id === 0 ? state.customBallSkin : (p.ballState.skin || 'neon'),
                            trailEffect: p.id === 0 ? state.customBallTrail : (p.ballState.trailEffect || 'neon')
                        }))
                    });
                }
                updateLobbyUI();
            }
            break;

        case 'players_list':
            console.log("Syncing players list from Host:", data.players);
            const clientColors = [
                { r: 100, g: 255, b: 100 }, // Player 2 Green
                { r: 100, g: 200, b: 255 }, // Player 3 Blue
                { r: 255, g: 100, b: 255 }  // Player 4 Pink
            ];
            
            const newPlayers = [];
            data.players.forEach(p => {
                const existing = mpState.players.find(x => x.id === p.id);
                const color = p.id === 0 ? { r: 255, g: 100, b: 100 } : (clientColors[p.id - 1] || { r: 255, g: 255, b: 255 });
                
                const finalColor = {
                    r: p.colorR !== undefined ? p.colorR : (existing ? existing.ballState.colorR : color.r),
                    g: p.colorG !== undefined ? p.colorG : (existing ? existing.ballState.colorG : color.g),
                    b: p.colorB !== undefined ? p.colorB : (existing ? existing.ballState.colorB : color.b)
                };
                
                newPlayers.push({
                    id: p.id,
                    name: p.id === mpState.playerId ? `${p.name} (You)` : p.name,
                    ready: p.ready,
                    finished: existing ? existing.finished : false,
                    completedAll: existing ? existing.completedAll : false,
                    strokes: existing ? existing.strokes : 0,
                    holeIndex: existing ? existing.holeIndex : 0,
                    finalStrokes: existing ? existing.finalStrokes : 0,
                    finalTime: existing ? existing.finalTime : 0,
                    ballState: existing ? existing.ballState : { x: 0, y: 0, vx: 0, vy: 0, r: 8, isSinking: false, colorR: finalColor.r, colorG: finalColor.g, colorB: finalColor.b, skin: p.skin || 'neon', trailEffect: p.trailEffect || 'neon', trail: [] }
                });
                
                if (existing) {
                    existing.ballState.colorR = finalColor.r;
                    existing.ballState.colorG = finalColor.g;
                    existing.ballState.colorB = finalColor.b;
                    if (p.skin !== undefined) existing.ballState.skin = p.skin;
                    if (p.trailEffect !== undefined) existing.ballState.trailEffect = p.trailEffect;
                }
            });
            mpState.players = newPlayers;
            updateLobbyUI();
            break;

        case 'room_full':
            alert("This room is full (max 4 players)!");
            disconnectMultiplayer();
            document.getElementById('mpConnectingMsg').style.display = 'none';
            document.getElementById('startScreen').classList.add('active');
            break;

        case 'ready':
            if (player) {
                player.ready = data.isReady;
                
                // Host broadcasts updated ready state to all clients
                if (mpState.role === 'host') {
                    sendPacket({
                        type: 'players_list',
                        players: mpState.players.map(p => ({ id: p.id, name: p.name, ready: p.ready }))
                    });
                }
                updateLobbyUI();
            }
            break;

        case 'init':
            console.log("Course levels received from Host!");
            mpState.courseLevels = data.levels;
            if (messageCallback) messageCallback(data);
            break;

        case 'start_match':
            console.log("Host started the match!");
            mpState.startTime = performance.now();
            if (messageCallback) messageCallback(data);
            break;

        case 'sync_ball':
            if (player && data.holeIndex === state.currentHoleIndex) {
                const isNewHole = player.holeIndex !== state.currentHoleIndex;
                const isFirstSync = player.ballState.x === 0 && player.ballState.y === 0;
                
                if (isNewHole || isFirstSync) {
                    player.ballState.spawnProtectionTimer = 30;
                    if (typeof window.triggerOpponentPopIn === 'function') {
                        window.triggerOpponentPopIn(data.x, data.y);
                    }
                }

                const incomingMoving = Math.hypot(data.vx, data.vy) > 0.1;
                const recentlyBumped = player.ballState.recentlyBumpedTimer > 0;
                if (!recentlyBumped || incomingMoving) {
                    const previouslyStationary = Math.hypot(player.ballState.vx, player.ballState.vy) < 0.1;
                    if (previouslyStationary && incomingMoving) {
                        player.ballState.impactTimer = 1.3;
                    }

                    player.ballState.x = data.x;
                    player.ballState.y = data.y;
                    player.ballState.vx = data.vx;
                    player.ballState.vy = data.vy;
                }

                player.ballState.isSinking = data.isSinking;
                player.ballState.isStickyShot = data.isStickyShot;
                player.ballState.homingActiveThisShot = data.homingActiveThisShot;
                
                if (data.colorR !== undefined) player.ballState.colorR = data.colorR;
                if (data.colorG !== undefined) player.ballState.colorG = data.colorG;
                if (data.colorB !== undefined) player.ballState.colorB = data.colorB;
                if (data.skin !== undefined) player.ballState.skin = data.skin;
                if (data.trailEffect !== undefined) player.ballState.trailEffect = data.trailEffect;
                
                if (data.holeIndex !== player.holeIndex) {
                    player.ballState.trail = [];
                }
            }
            if (player) {
                player.holeIndex = data.holeIndex;
            }
            break;

        case 'stroke':
            if (player && data.holeIndex === player.holeIndex) {
                player.strokes = data.strokes;
                if (typeof window.triggerHUDHitFlash === 'function') {
                    window.triggerHUDHitFlash(player.id);
                }
            }
            break;

        case 'sink':
            console.log(`Player ${senderId} sank ball on Hole ${data.holeIndex + 1} with ${data.strokes} strokes.`);
            if (player && data.holeIndex === state.currentHoleIndex) {
                player.ballState.isSinking = true;
            }
            break;

        case 'collision':
            if (data.holeIndex === state.currentHoleIndex && data.targetPlayerId === mpState.playerId) {
                const primaryBall = state.gameState.balls[0];
                if (primaryBall) {
                    primaryBall.x = data.localX;
                    primaryBall.y = data.localY;
                    primaryBall.vx = data.localVx;
                    primaryBall.vy = data.localVy;
                    primaryBall.moving = true;
                }
            }
            break;

        case 'finish':
            if (player) {
                console.log(`Player ${senderId} (${player.name}) finished! Strokes: ${data.totalStrokes}, Time: ${data.totalTime.toFixed(1)}s`);
                player.finished = true;
                player.completedAll = true;
                player.finalStrokes = data.totalStrokes;
                player.finalTime = data.totalTime;
                
                if (messageCallback) messageCallback(data);
            }
            break;
    }
}

export function sendPacket(packet) {
    if (state.isBotGame) return;
    packet.playerId = mpState.playerId;
    
    if (mpState.role === 'host') {
        mpState.connections.forEach(conn => {
            if (conn.open) {
                try { conn.send(packet); } catch(e) {}
            }
        });
    } else if (mpState.conn && mpState.isConnected) {
        try {
            mpState.conn.send(packet);
        } catch (e) {
            console.error("Failed to send WebRTC packet:", e);
        }
    }
}

export function updateLobbyUI() {
    const container = document.getElementById('mpLobbyCardsContainer');
    if (!container) return;
    container.innerHTML = '';

    for (let slot = 0; slot < 4; slot++) {
        const player = mpState.players.find(p => p.id === slot);
        const card = document.createElement('div');
        
        if (player) {
            const isLocal = player.id === mpState.playerId;
            const slotColor = player.id === 0 ? 'text-rose-400' : (player.id === 1 ? 'text-green-400' : (player.id === 2 ? 'text-cyan-400' : 'text-purple-400'));
            
            let statusText = 'WAITING';
            let statusClass = 'joined';
            if (player.id === 0) {
                statusText = 'HOSTING';
                statusClass = 'ready';
            } else if (player.ready) {
                statusText = 'READY';
                statusClass = 'ready';
            }
            
            card.className = `player-card connected p-3 rounded-lg border border-gray-800 bg-gray-950 text-left transform transition-transform hover:scale-[1.02]`;
            card.innerHTML = `
                <p class="text-[10px] ${slotColor} font-bold uppercase tracking-wider">Player ${slot + 1} ${isLocal ? '(You)' : ''}</p>
                <p class="text-base font-bold text-white mb-2 truncate">${player.name}</p>
                <span class="player-status ${statusClass}">${statusText}</span>
            `;
        } else {
            card.className = `player-card waiting p-3 rounded-lg border border-dashed border-gray-800 bg-gray-950/20 text-left flex flex-col justify-center items-center h-[96px]`;
            card.innerHTML = `
                <p class="text-[10px] text-gray-600 font-bold uppercase tracking-wider mb-1">Player ${slot + 1}</p>
                <p class="text-xs text-gray-500 font-medium">WAITING FOR PLAYER...</p>
            `;
        }
        container.appendChild(card);
    }
}

export function disconnectMultiplayer() {
    console.log("Disconnecting PeerJS WebRTC connections...");
    if (mpState.connections && mpState.connections.length > 0) {
        mpState.connections.forEach(conn => {
            try { conn.close(); } catch(e) {}
        });
        mpState.connections = [];
    }
    if (mpState.conn) {
        try { mpState.conn.close(); } catch(e) {}
        mpState.conn = null;
    }
    if (mpState.peer) {
        try { mpState.peer.destroy(); } catch(e) {}
        mpState.peer = null;
    }
    mpState.isMultiplayer = false;
    mpState.isConnected = false;
    mpState.role = null;
    mpState.players = [];
}
